// Conversation history page styles (WEB-8002). Composed by workspaceStyles.ts
// at the section's original position — source order is part of the cascade.
export const chatHistory = `
/* ===== History page ===== */
.ws-history {
  flex: 1;
  overflow: auto;
  /* Reserve the scrollbar gutter so content doesn't shift sideways when a
     scrollbar appears (e.g. after "Show more" in the Inbox). */
  scrollbar-gutter: stable;
}
.ws-history-inner {
  max-width: 720px;
  margin: 0 auto;
  padding: 24px 32px 48px;
}
.ws-history-title {
  font-family: var(--ws-sans);
  font-size: 24px;
  font-weight: 500;
  letter-spacing: -0.01em;
  margin: 0 0 16px;
  color: var(--ws-ink);
}
.ws-history-search {
  display: flex; align-items: center; gap: 8px;
  background: #ffffff;
  border: 0.5px solid var(--ws-line-2);
  border-radius: var(--ws-r-md);
  padding: 8px 12px;
  margin-bottom: 24px;
  color: var(--ws-ink-4);
}
.ws-history-search input {
  border: 0; background: transparent; outline: none;
  flex: 1; color: var(--ws-ink); font-size: 13px;
  font-family: var(--ws-sans);
}
.ws-history-empty {
  padding: 32px 0; text-align: center; color: var(--ws-ink-4); font-size: 13px;
}
.ws-history-group { margin-bottom: 18px; }
.ws-history-group-label {
  font-size: 10.5px; font-weight: 600;
  color: var(--ws-ink-4); letter-spacing: 0.1em;
  text-transform: uppercase;
  padding: 0 4px 6px;
}
.ws-history-list { display: flex; flex-direction: column; gap: 1px; }
.ws-history-row {
  display: flex; align-items: center; gap: 10px;
  padding: 9px 12px;
  border: 0; background: transparent;
  border-radius: 7px;
  font-family: var(--ws-sans); font-size: 13.5px;
  color: var(--ws-ink-2);
  cursor: pointer; text-align: left;
  transition: background 0.12s;
}
.ws-history-row:hover { background: rgba(0,0,0,0.035); }
.ws-history-row.active { background: rgba(0,0,0,0.06); color: var(--ws-ink); font-weight: 500; }
.ws-history-row-title { flex: 1; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.ws-history-row-date { color: var(--ws-ink-5); font-size: 11.5px; flex-shrink: 0; }
/* WEB-6799: Inbox feed — compact rows grouped into Needs you / Unread / Earlier. */
/* WEB-7943: "Mark all as read" sits on the Unread group-label row, right-aligned, as a text link. */
.ws-inbox-group-label--action {
  display: flex; align-items: center; justify-content: space-between; gap: 12px;
}
.ws-inbox-markall-link {
  flex-shrink: 0;
  display: inline-flex; align-items: center; gap: 5px;
  background: none; border: 0; padding: 0; cursor: pointer; white-space: nowrap;
  color: var(--ws-accent); font-family: var(--ws-sans);
  font-size: 12px; font-weight: 500; letter-spacing: 0; text-transform: none;
}
.ws-inbox-markall-link:hover { text-decoration: underline; text-underline-offset: 2px; }
/* Per-row "mark as read" — a quiet icon button that clears one unread row. */
.ws-inbox-mark {
  flex-shrink: 0;
  display: inline-flex; align-items: center; justify-content: center;
  width: 24px; height: 24px; padding: 0;
  border: 0; border-radius: 6px; background: transparent; cursor: pointer;
  color: var(--ws-ink-4);
}
.ws-inbox-mark:hover { background: rgba(0,0,0,0.06); color: var(--ws-accent); }
.ws-inbox-section { margin-bottom: 22px; }
/* Extra breathing room before "Earlier" so it reads as a separate, lower-priority zone. */
.ws-inbox-section--spaced { margin-top: 40px; }
.ws-inbox-showmore {
  margin: 8px 0 0 8px;
  background: none; border: none; cursor: pointer; padding: 6px 2px;
  color: var(--ws-accent); font-size: 12.5px; font-weight: 500;
}
.ws-inbox-showmore:hover { text-decoration: underline; }
.ws-inbox-section-count {
  margin-left: 8px;
  display: inline-flex; align-items: center; justify-content: center;
  min-width: 17px; height: 17px; padding: 0 5px;
  border-radius: 9px;
  background: var(--ws-line);
  color: var(--ws-ink-4);
  font-size: 11px; font-weight: 600; letter-spacing: 0;
  font-variant-numeric: tabular-nums;
  vertical-align: middle;
}
.ws-inbox-list { display: flex; flex-direction: column; }
.ws-inbox-item { border-bottom: 1px solid var(--ws-line); border-radius: 8px; }
.ws-inbox-item.active { background: rgba(0,0,0,0.05); }
.ws-inbox-item--earlier { opacity: 0.6; }
.ws-inbox-item--earlier:hover { opacity: 1; }
/* Clicking the row toggles the inline preview; the header link opens the thread. */
.ws-inbox-row {
  display: flex; align-items: flex-start; gap: 14px;
  width: 100%;
  padding: 14px 8px;
  cursor: pointer; text-align: left;
  border-radius: 8px;
}
.ws-inbox-row:hover { background: rgba(0,0,0,0.025); }
.ws-inbox-row:focus-visible { outline: 2px solid var(--ws-accent); outline-offset: -2px; }
/* Icon + label link to the conversation, pinned to the right of the title line. */
.ws-inbox-link {
  margin-left: auto; flex-shrink: 0;
  display: inline-flex; align-items: center; gap: 5px;
  color: var(--ws-accent);
  font-size: 12.5px; font-weight: 500;
  text-decoration: none; white-space: nowrap;
}
.ws-inbox-link:hover { text-decoration: underline; text-underline-offset: 2px; }
/* WEB-8800: in a narrow row (the resizable floating chat) the nowrap link starved the
   title down to one character. Below this width the label drops and the icon alone
   links to the thread; the aria-label/title keep it named. */
@container ws-inbox-head (max-width: 250px) {
  .ws-inbox-link-label { display: none; }
}
.ws-inbox-icon {
  flex-shrink: 0;
  width: 34px; height: 34px;
  border-radius: 9px;
  display: grid; place-items: center;
}
.ws-inbox-icon--proposal { background: #fbedd3; color: #b9821f; }
.ws-inbox-icon--routine { background: #ece9fb; color: #6b5bd1; }
.ws-inbox-icon--message { background: #e3edfb; color: #3f6fbf; }
.ws-inbox-icon--failed { background: #fbe3e3; color: #c0392b; }
/* Size container for the title line — its width is set by the flex line (flex:1 + min-width:0),
   so inline-size containment is safe here. Drives the icon-only link above. */
.ws-inbox-row-main {
  flex: 1; min-width: 0; display: flex; flex-direction: column; gap: 3px;
  container-type: inline-size; container-name: ws-inbox-head;
}
.ws-inbox-row-head { display: flex; align-items: center; gap: 9px; min-width: 0; }
.ws-inbox-row-title {
  font-size: 15px; font-weight: 600; color: var(--ws-ink);
  overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
  min-width: 0; max-width: 100%;
}
.ws-inbox-tag {
  flex-shrink: 0;
  font-size: 11px; font-weight: 600;
  padding: 1px 7px; border-radius: 5px;
  white-space: nowrap;
}
.ws-inbox-tag--proposal { background: #fbedd3; color: #b9821f; }
.ws-inbox-tag--failed { background: #fbe3e3; color: #c0392b; }
.ws-inbox-row-preview {
  font-size: 13.5px; color: var(--ws-ink-4); line-height: 1.45;
  overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
  max-width: 100%;
}
.ws-inbox-meta {
  flex-shrink: 0; display: flex; align-items: center; gap: 8px;
  padding-top: 3px;
}
.ws-inbox-dot-slot { width: 7px; flex-shrink: 0; display: flex; justify-content: center; }
.ws-inbox-dot { width: 7px; height: 7px; border-radius: 50%; background: var(--ws-accent); }
.ws-inbox-when {
  width: 42px; flex-shrink: 0; text-align: right;
  font-size: 12.5px; color: var(--ws-ink-5); font-variant-numeric: tabular-nums;
}
.ws-inbox-chev-slot { width: 14px; flex-shrink: 0; display: flex; justify-content: center; }
/* Expanded message body — aligns under the title (icon 34 + gap 14 + pad 8). */
.ws-inbox-item-body { padding: 0 12px 14px 56px; }
/* Scroll container only — text styling comes from pr-prose so it matches chat. */
.ws-inbox-message { max-height: 360px; overflow-y: auto; padding-bottom: 8px; }
.ws-sb-item .ws-ic { width: 16px; height: 16px; flex-shrink: 0; color: currentColor; opacity: 0.85; }
.ws-sb-item .ws-count {
  margin-left: auto;
  font-size: 11px;
  background: var(--ws-ink);
  color: #e8f9f8;
  min-width: 18px; height: 18px;
  border-radius: 9px;
  display: grid; place-items: center;
  padding: 0 6px;
  font-weight: 600;
  font-variant-numeric: tabular-nums;
}
.ws-sb-item .ws-count.muted {
  background: transparent;
  color: var(--ws-ink-4);
  font-weight: 500;
}
.ws-sb-item .ws-title-text {
  overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
  flex: 1; min-width: 0;
}

.ws-agent-swatch {
  width: 16px; height: 16px;
  border-radius: 5px;
  display: grid; place-items: center;
  font-family: var(--ws-serif);
  font-size: 8px;
  font-weight: 700;
  color: white;
  flex-shrink: 0;
  overflow: hidden;
}
.ws-agent-swatch img { width: 100%; height: 100%; object-fit: cover; display: block; }
.ws-agent-swatch:has(img) { background: transparent; }

.ws-pill-dark {
  margin-left: auto;
  font-size: 10px;
  padding: 1px 6px;
  border-radius: 999px;
  color: #e8f9f8;
  background: var(--ws-ink);
  font-weight: 600;
  letter-spacing: 0.02em;
}

.ws-sb-conversations {
  flex: 1 1 auto;
  min-height: 0;
}
.ws-sb-group { margin-top: 2px; }
.ws-sb-group-head {
  display: flex; align-items: center; gap: 6px;
  padding: 5px 10px;
  font-size: 11px; font-weight: 600;
  color: var(--ws-ink-4);
  letter-spacing: 0.04em;
  border: 0; background: transparent;
  cursor: pointer; width: 100%;
  text-align: left;
  font-family: var(--ws-sans);
}
.ws-sb-group-head:hover { color: var(--ws-ink-3); }
.ws-sb-group-count {
  margin-left: auto;
  font-size: 10px;
  color: var(--ws-ink-5);
  font-weight: 500;
}
.ws-sb-chev {
  display: inline-flex; align-items: center;
  transition: transform 0.12s;
  color: var(--ws-ink-5);
}
.ws-sb-chev.open { transform: rotate(90deg); }
.ws-sb-paused {
  margin-left: auto;
  font-size: 10px;
  color: var(--ws-ink-4);
  font-weight: 500;
  font-style: italic;
}

.ws-sb-unread-dot {
  position: absolute;
  left: 10px;
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #C62828;
}
.ws-sb-unread-dot-inline {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #C62828;
  flex-shrink: 0;
  margin-left: 4px;
}

.ws-sb-footer {
  flex-shrink: 0;
  padding-top: 10px;
  border-top: 1px solid var(--ws-line);
}
.ws-sb-footer-section {
  border-bottom: 1px solid var(--ws-line);
  padding-bottom: 4px;
  margin-bottom: 4px;
}
.ws-sb-footer-section > .ws-sb-item {
  position: relative;
  padding-left: 22px;
}
`;
